import { useEffect, useRef, useState } from "react";

export type FilterProps = {
  initialOpen?: boolean;
  onRemove: (value: string) => void;
};

type FilterMultiselectProps<T> = FilterProps & {
  id: string;
  label: string;
  options: T[];
  value: T[];
  getDisplayValue: (value: T) => string;
  getSelectedDisplayValue: (selected: T[]) => string;
  onChange: (value: readonly T[]) => void;
  onClose?: () => void;
  direction?: "row" | "column";
};

/**
 * FilterMultiselect component - Multi-select filter with chip display and removal
 *
 * Adapted from operations-iol-webapp for Tailwind CSS styling.
 * Uses Material Symbols for icons.
 *
 * @param direction - "row" for button style, "column" for chip style (default: "row")
 */
export default function FilterMultiselect<T>({
  id,
  label,
  options,
  value,
  initialOpen = false,
  getDisplayValue,
  getSelectedDisplayValue,
  onChange,
  onClose,
  onRemove,
  direction = "row",
}: FilterMultiselectProps<T>) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [searchText, setSearchText] = useState("");

  // React to initialOpen prop changes (for auto-open on add)
  useEffect(() => {
    if (initialOpen) {
      setIsOpen(true);
    }
  }, [initialOpen]);

  // Focus input when popper opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setSearchText("");
    onClose?.();
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        popperRef.current &&
        anchorRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        !anchorRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchText("");
        onClose?.();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);

  // Handle keyboard events
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      handleClose();
      anchorRef.current?.focus();
    }
  };

  const handleOptionToggle = (option: T) => {
    const isSelected = value.some((v) => getDisplayValue(v) === getDisplayValue(option));

    if (isSelected) {
      onChange(value.filter((v) => getDisplayValue(v) !== getDisplayValue(option)));
    } else {
      onChange([...value, option]);
    }
  };

  // Filter options by search text
  const filteredOptions = options.filter((option) =>
    getDisplayValue(option).toLowerCase().includes(searchText.toLowerCase())
  );

  const filterId = `filter-${id}`;

  const dropdownContent = isOpen && (
    <div
      ref={popperRef}
      id={filterId}
      role="dialog"
      aria-modal="true"
      onKeyDown={handleKeyDown}
      className={`
        absolute left-0 top-full z-50 mt-1
        w-[300px] max-h-[400px]
        bg-white dark:bg-[#192233]
        border border-gray-200 dark:border-[#324467]
        rounded-lg shadow-lg
        overflow-hidden
      `}
    >
      {/* Search input */}
      <div className="p-2 border-b border-gray-200 dark:border-[#324467]">
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search"
            className={`
              w-full px-3 py-2 pr-10
              text-sm text-gray-900 dark:text-white
              bg-white dark:bg-[#111722]
              border border-gray-300 dark:border-[#324467]
              rounded-md
              focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary
            `}
          />
          <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg">
            search
          </span>
        </div>
      </div>

      {/* Options list */}
      <div className="max-h-[300px] overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No results found</div>
        ) : (
          filteredOptions.map((option) => {
            const displayValue = getDisplayValue(option);
            const isSelected = value.some((v) => getDisplayValue(v) === displayValue);

            return (
              <button
                key={displayValue}
                type="button"
                onClick={() => handleOptionToggle(option)}
                className={`
                  w-full flex items-center gap-3 px-4 py-2
                  text-sm text-left
                  text-gray-700 dark:text-gray-200
                  hover:bg-gray-100 dark:hover:bg-gray-700
                  transition-colors duration-150
                `}
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => {}}
                  className={`
                    h-4 w-4 rounded
                    text-primary
                    border-gray-300 dark:border-gray-600
                    focus:ring-primary/50
                  `}
                />
                {displayValue}
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <>
      {direction === "row" ? (
        <ButtonFilterItem
          anchorRef={anchorRef}
          filterId={filterId}
          id={id}
          label={label}
          onRemove={onRemove}
          isOpen={isOpen}
          onToggle={handleToggle}
          value={value}
          getSelectedDisplayValue={getSelectedDisplayValue}
          dropdownContent={dropdownContent}
        />
      ) : (
        <ChipFilterItem
          anchorRef={anchorRef}
          filterId={filterId}
          id={id}
          label={label}
          onRemove={onRemove}
          isOpen={isOpen}
          onToggle={handleToggle}
          value={value}
          getSelectedDisplayValue={getSelectedDisplayValue}
          dropdownContent={dropdownContent}
        />
      )}
    </>
  );
}

// Button-style filter item (for row direction)
interface FilterItemProps<T> {
  anchorRef: React.RefObject<HTMLButtonElement>;
  filterId: string;
  id: string;
  label: string;
  onRemove: (value: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  value: T[];
  getSelectedDisplayValue: (selected: T[]) => string;
  dropdownContent: React.ReactNode;
}

function ButtonFilterItem<T>({
  anchorRef,
  filterId,
  id,
  label,
  onRemove,
  isOpen,
  onToggle,
  value,
  getSelectedDisplayValue,
  dropdownContent,
}: FilterItemProps<T>) {
  return (
    <div className="relative flex items-center">
      <button
        ref={anchorRef}
        type="button"
        aria-label={`Filter by ${label}`}
        aria-describedby={filterId}
        onClick={onToggle}
        className={`
          flex items-center gap-1 px-3 py-1.5 pr-8
          text-sm font-medium
          ${isOpen ? "bg-blue-100 dark:bg-blue-900/30" : "hover:bg-gray-100 dark:hover:bg-gray-700"}
          rounded-md transition-colors duration-150
        `}
      >
        <span className="font-semibold text-gray-700 dark:text-gray-200">{label}:</span>
        <span className="font-bold text-gray-900 dark:text-white">
          {getSelectedDisplayValue(value)}
        </span>
        <span className="material-symbols-outlined text-gray-500 text-lg">arrow_drop_down</span>
      </button>
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        title={`Remove ${label} filter`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(id);
        }}
        className={`
          absolute right-1 p-0.5
          text-gray-400 hover:text-gray-600
          dark:text-gray-500 dark:hover:text-gray-300
          transition-colors duration-150
        `}
      >
        <span className="material-symbols-outlined text-base">cancel</span>
      </button>
      {dropdownContent}
    </div>
  );
}

// Chip-style filter item (for column direction)
function ChipFilterItem<T>({
  anchorRef,
  filterId,
  id,
  label,
  onRemove,
  isOpen,
  onToggle,
  value,
  getSelectedDisplayValue,
  dropdownContent,
}: FilterItemProps<T>) {
  const valueDisplay =
    value.length === 0
      ? "All"
      : value.length === 1
        ? getSelectedDisplayValue([value[0]])
        : getSelectedDisplayValue([value[0]]);

  return (
    <div className="relative flex items-center">
      <button
        ref={anchorRef}
        type="button"
        aria-label={`Filter by ${label}`}
        aria-describedby={filterId}
        onClick={onToggle}
        className={`
          relative flex items-center gap-2 px-3 py-1.5 pr-7
          text-xs font-medium
          ${isOpen ? "bg-blue-100 dark:bg-blue-900/30" : "bg-white dark:bg-[#232f48]"}
          border border-gray-200 dark:border-[#324467]
          rounded-full
          hover:bg-gray-50 dark:hover:bg-gray-700
          transition-colors duration-150
          ${value.length > 1 ? "border-2 border-primary/50" : ""}
        `}
      >
        {value.length > 1 && (
          <span
            className={`
              absolute -right-2 -top-1
              min-w-[1rem] h-4 px-1
              flex items-center justify-center
              text-[10px] font-bold text-white
              bg-primary rounded-full
              shadow-sm
            `}
          >
            +{value.length - 1}
          </span>
        )}
        <span className="font-semibold text-gray-600 dark:text-gray-300">{label}:</span>
        <span className="text-gray-600 dark:text-gray-300">{valueDisplay}</span>
      </button>
      <button
        type="button"
        aria-label={`Remove ${label} filter`}
        title={`Remove ${label} filter`}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(id);
        }}
        className={`
          absolute right-1.5 p-0
          text-gray-400 hover:text-gray-600
          dark:text-gray-500 dark:hover:text-gray-300
          transition-colors duration-150
        `}
      >
        <span className="material-symbols-outlined text-sm">close</span>
      </button>
      {dropdownContent}
    </div>
  );
}
